import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Phone } from "lucide-react";
import React from "react";
import { useGetAiAgentQuery, useGetAiAgentCallsQuery } from "@/store/rest_api";
import { AiAgentCall } from "@/model/interface/AiAgent";
import CallHistoryRow from "./CallHistoryRow";
import TranscriptDialog from "./TranscriptDialog";

type DirectionFilter = "ALL" | "INBOUND" | "OUTBOUND";

function EmptyCallList() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Phone className="h-8 w-8 text-muted-foreground mb-3" />
      <p className="text-muted-foreground">No calls recorded yet</p>
    </div>
  );
}

function LoadingCallList() {
  return <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>;
}

function CallHistoryTable({
  calls,
  onOpenTranscript,
}: {
  calls: AiAgentCall[];
  onOpenTranscript: (callId: string) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Direction</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead>Outcome</TableHead>
          <TableHead>Sentiment</TableHead>
          <TableHead>Summary</TableHead>
          <TableHead>Cost</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {calls.map((call) => (
          <CallHistoryRow key={call.id} call={call} onOpenTranscript={onOpenTranscript} />
        ))}
      </TableBody>
    </Table>
  );
}

function DirectionFilterSelect({
  value,
  onChange,
}: {
  value: DirectionFilter;
  onChange: (next: DirectionFilter) => void;
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as DirectionFilter)}>
      <SelectTrigger className="w-40">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">All directions</SelectItem>
        <SelectItem value="INBOUND">Inbound only</SelectItem>
        <SelectItem value="OUTBOUND">Outbound only</SelectItem>
      </SelectContent>
    </Select>
  );
}

export default function AiAgentCallsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: agent } = useGetAiAgentQuery(id!);

  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>("ALL");
  const [transcriptCallId, setTranscriptCallId] = useState<string | null>(null);

  const { data: callsPage, isLoading } = useGetAiAgentCallsQuery({
    agentId: id!,
    direction: directionFilter === "ALL" ? undefined : directionFilter,
  });

  const calls = callsPage?.items ?? [];

  const handleDialogChange = (isOpen: boolean) => {
    if (!isOpen) {
      setTranscriptCallId(null);
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/ai-agents")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Call History</h1>
          <p className="text-muted-foreground">{agent?.name ?? "Agent"}</p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Recent Calls</CardTitle>
            <CardDescription>{callsPage?.totalElements ?? 0} total calls</CardDescription>
          </div>
          <DirectionFilterSelect value={directionFilter} onChange={setDirectionFilter} />
        </CardHeader>
        <CardContent>
          {isLoading && <LoadingCallList />}
          {!isLoading && calls.length === 0 && <EmptyCallList />}
          {!isLoading && calls.length > 0 && (
            <CallHistoryTable calls={calls} onOpenTranscript={setTranscriptCallId} />
          )}
        </CardContent>
      </Card>

      <TranscriptDialog
        agentId={id!}
        callId={transcriptCallId}
        open={transcriptCallId !== null}
        onOpenChange={handleDialogChange}
      />
    </div>
  );
}
